import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  FaMagnifyingGlass,
  FaCircleInfo,
  FaBoxesStacked,
  FaMobileScreenButton,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import "../Dashboard.css";
import "../styles/StockLookup.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PreloadFetch from "../components/PreloadFetch";
const money = (v) =>
  new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
  }).format(Number(v || 0));
const uniq = (a) => [...new Set(a.filter(Boolean))];
const productName = (brand = "", model = "", fallback = "") => {
  const b = String(brand || "").trim();
  const m = String(model || "").trim();
  if (!b && !m) return String(fallback || "").trim();
  if (!b) return m;
  if (!m) return b;

  // Evita duplicados visuales como "REDMI REDMI 15C" o "POCO POCO X8"
  // sin modificar los datos guardados en Inventario.
  const bUpper = b.toUpperCase();
  const mUpper = m.toUpperCase();
  if (mUpper === bUpper || mUpper.startsWith(`${bUpper} `)) return m;
  return `${b} ${m}`;
};
const sedeCity = (name = "") => {
  const n = String(name || "").trim().toUpperCase();
  if (!n) return "";
  // Los almacenes centrales conservan la ciudad al final del nombre.
  const central = n.match(/\b(PUCALLPA|IQUITOS|TARAPOTO|CONTAMANA|HUANUCO|TINGO\s*MARIA)\b/);
  if (central) return central[1].replace(/\s+/g, " ");
  return n.split(/\s+/)[0];
};
function StockLookup() {
  const [search, setSearch] = useState(""),
    [loading, setLoading] = useState(false),
    [result, setResult] = useState(null),
    [expanded, setExpanded] = useState({});
  const run = async (e) => {
    e?.preventDefault();
    const q = search.trim();
    if (q.length < 2) {
      toast.info(
        "Escribe un IMEI, los últimos 6 dígitos, una marca o un modelo.",
      );
      return;
    }
    try {
      setLoading(true);
      const r = await axios.get(`${process.env.REACT_APP_API}/stock-search`, {
        params: { q },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setResult(r.data);
      setExpanded({});
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "No se pudo consultar el stock.",
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  };
  const sedes = useMemo(() => {
    if (result?.mode !== "MODEL") return [];
    const sm = new Map();
    result.data.forEach((x) => {
      const k = `l${x.locationId}`; // No fusionar almacén y tienda de la misma sede
      if (!sm.has(k))
        sm.set(k, {
          key: k,
          name: x.sedeName || x.locationName || "Ubicación",
          isCurrent: !!x.isCurrentSede,
          city: x.locationCity || sedeCity(x.sedeName || x.locationName || ""),
          priority: Number.isInteger(Number(x.locationPriority)) ? Number(x.locationPriority) : (x.isCurrentSede ? 0 : 2),
          units: [],
        });
      sm.get(k).units.push(x);
    });
    return [...sm.values()]
      .map((s) => {
        const vm = new Map();
        s.units.forEach((u) => {
          const k = `${u.model || u.productTitle}|${u.ram || "-"}|${u.rom || "-"}`;
          if (!vm.has(k)) vm.set(k, { key: k, units: [] });
          vm.get(k).units.push(u);
        });
        s.variants = [...vm.values()]
          .sort(
            (a, b) =>
              b.units.length - a.units.length || a.key.localeCompare(b.key),
          )
          .slice(0, 3);
        return s;
      })
      .sort((a, b) => {
        return a.priority - b.priority || a.city.localeCompare(b.city) || a.name.localeCompare(b.name);
      });
  }, [result]);
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));
  return (
    <div>
      <Header />
      {loading && <PreloadFetch />}
      <main className="stock-lookup-page">
        <section className="stock-lookup-hero">
          <div>
            <span>CONSULTA COMERCIAL</span>
            <h1>Consulta de Stock</h1>
            <p>
              Encuentra equipos disponibles por IMEI, marca o modelo y consulta
              su sede y precio actual.
            </p>
          </div>
          <div className="source">
            <FaBoxesStacked /> Inventario en tiempo real
          </div>
        </section>
        <section className="stock-lookup-search-card">
          <form onSubmit={run}>
            <FaMagnifyingGlass />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="IMEI completo, últimos 6 dígitos, marca o modelo..."
            />
            <button disabled={loading}>Buscar stock</button>
          </form>
          <div className="examples">
            Ejemplos: <b>HONOR X8D</b> · <b>SAMSUNG A56</b> · <b>483921</b>
          </div>
          <div className="tip">
            <FaCircleInfo />
            <div>
              <b>Para encontrar el modelo correcto:</b> busca{" "}
              <strong>REDMI</strong> sin “Xiaomi”, <strong>POCO</strong> sin
              “Xiaomi” y <strong>NUBIA</strong> sin “ZTE”.
            </div>
          </div>
        </section>
        {!result && !loading && (
          <section className="lookup-empty">
            <FaMobileScreenButton />
            <h2>Busca un equipo para consultar disponibilidad</h2>
            <p>
              Los resultados se obtienen directamente de Inventario. Esta página
              no mantiene precios propios.
            </p>
          </section>
        )}
        {result?.correctedQuery && (
          <div className="correction">
            Búsqueda interpretada como <b>{result.correctedQuery}</b>.
          </div>
        )}
        {result && !result.hasResults && (!result.data || result.data.length === 0) && (
          <section className="lookup-empty compact">
            <h2>No encontramos stock disponible</h2>
            <p>
              Revisa la marca/modelo o confirma los últimos 6 dígitos del IMEI.
            </p>
          </section>
        )}
        {result?.mode === "IMEI" && result.total > 0 && (
          <section className="stock-result-block">
            <div className="result-title">
              <span>RESULTADO POR IMEI</span>
              <h2>{result.total} equipo(s) disponible(s)</h2>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>IMEI</th>
                    <th>Sede</th>
                    <th>Precio</th>
                    <th>Contado</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {result.data.map((x) => (
                    <tr key={x.serialId}>
                      <td>
                        <b>{x.productTitle}</b>
                      </td>
                      <td className="imei">{x.imei1 || x.imei2 || "-"}</td>
                      <td>
                        {x.sedeName || x.locationName}
                        {x.isCurrentSede && <em>Tu sede</em>}
                      </td>
                      <td className="price">{money(x.salePrice)}</td>
                      <td>{money(x.cashPrice)}</td>
                      <td>
                        <span className="available">Disponible</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
        {result?.mode === "MODEL" && result.hasResults && result.data?.length > 0 && (
          <section className="model-results">
            <div className="result-summary">
              <div>
                <span>RESULTADOS PARA</span>
                <h2>{result.query}</h2>
              </div>
              <b>Disponibilidad nacional por sede y almacén</b>
            </div>
            {sedes.map((s, si) => (
              <section
                className={`sede-card ${s.isCurrent ? "current" : ""}`}
                key={s.key}
              >
                <header>
                  <div>
                    <span>
                      {s.isCurrent
                        ? "★ TU SEDE"
                        : s.priority === 1
                          ? "OTRA SEDE DE TU CIUDAD"
                          : "OTRA CIUDAD"}
                    </span>
                    <h3>{s.name}</h3>
                    <small>{s.city || "Ciudad no registrada"}</small>
                  </div>
                  <b>Stock disponible</b>
                </header>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Capacidad</th>
                        <th>Colores / detalle</th>
                        <th>Crédito</th>
                        <th>Contado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.variants.map((v) => {
                        const u = v.units[0],
                          prices = v.units.map((z) => z.salePrice),
                          min = Math.min(...prices),
                          max = Math.max(...prices),
                          colors = uniq(v.units.map((z) => z.color));
                        const key = `${s.key}-${v.key}`;
                        return (
                          <React.Fragment key={key}>
                            <tr>
                              <td>
                                <b>
                                  {productName(u.brand, u.model, u.productTitle)}
                                </b>
                              </td>
                              <td>
                                <span className="capacity">
                                  {u.ram || "?"} / {u.rom || "?"}
                                </span>
                              </td>
                              <td>{colors.slice(0, 4).join(" · ") || "-"}</td>
                              <td className="price">
                                {min === max
                                  ? money(min)
                                  : `${money(min)} – ${money(max)}`}
                              </td>
                              <td>
                                {min === max
                                  ? money(min - 10)
                                  : `${money(min - 10)} – ${money(max - 10)}`}
                              </td>
                              <td>
                                <button
                                  className="detail-btn"
                                  onClick={() => toggle(key)}
                                >
                                  {expanded[key] ? "Ocultar" : "Ver equipos"}
                                </button>
                              </td>
                            </tr>
                            {expanded[key] && (
                              <tr className="detail-row">
                                <td colSpan="6">
                                  <div className="unit-grid">
                                    {v.units.slice(0, 3).map((z) => (
                                      <div className="unit" key={z.serialId}>
                                        <span>EQUIPO DISPONIBLE</span>
                                        <b>{z.productTitle}</b>
                                        <small>{z.color || "-"}</small>
                                        <strong>{money(z.salePrice)}</strong>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {s.variants.length < 3 && (
                  <p className="variant-note">
                    Se muestran {s.variants.length} capacidad(es) porque son las
                    variantes disponibles en esta sede.
                  </p>
                )}
              </section>
            ))}
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
export default StockLookup;
